import { type ReactNode } from 'react'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Pill } from '@/components/Pill'
import { CheckboxCell, DateCell, TableCell, TextCell } from '@/components/Table'
import { ImageCellContent } from '@/components/Table/TableCell'
import type { Id } from '@/infra/id'
import type { UserDataType } from '@/features/iam/users/UserData'
import { getUserDataTypeDisplayName } from '@/features/user-management/user-data-type-display'

interface UserManagementNameCellWithIndicatorProps {
  title?: string | null
  image?: string | null
  imageFallback?: string | null
  size?: number
  minWidth?: number
  noPadding?: boolean
  testId?: string
  online?: boolean
}

function UserManagementNameCellWithIndicator({
  title,
  image,
  imageFallback,
  size,
  minWidth,
  noPadding,
  testId,
  online,
}: UserManagementNameCellWithIndicatorProps) {
  return (
    <TableCell testId={testId}>
      <ImageCellContent
        title={title}
        image={image}
        size={size}
        imageFallback={imageFallback}
        minWidth={minWidth}
        noPadding={noPadding}
        online={online}
      />
    </TableCell>
  )
}

export interface UserManagementTableRowProps {
  publicId: Id
  displayName: string
  online: boolean
  email: string
  userDataType: UserDataType
  isWorkspaceCustomer: boolean
  lastSeen: Date | null
  dateCreated: Date
  isSelected: boolean
  onToggleSelected: (checked: boolean) => void
  actions: ReactNode
}

export function UserManagementTableRow({
  publicId,
  displayName,
  online,
  email,
  userDataType,
  isWorkspaceCustomer,
  lastSeen,
  dateCreated,
  isSelected,
  onToggleSelected,
  actions,
}: UserManagementTableRowProps) {
  return (
    <>
      <CheckboxCell
        testId={`user-management-table-row-toggle-selection-${publicId}`}
        checked={isSelected}
        onChange={onToggleSelected}
      />

      <UserManagementNameCellWithIndicator
        key={publicId}
        title={displayName}
        testId={`user-management-table-row-user-${publicId}`}
        online={online}
      />

      <TextCell>{email}</TextCell>

      <TableCell>
        {userDataType && (
          <Pill color={isWorkspaceCustomer ? 'green' : undefined}>
            {getUserDataTypeDisplayName(userDataType)}
          </Pill>
        )}
      </TableCell>

      <DateCell date={lastSeen} />

      <DateCell date={dateCreated} />

      <TableCell>{actions}</TableCell>
    </>
  )
}

export interface UserRowActionsProps {
  publicId: Id
  canManageGlobalCapacity: boolean
  isWorkspaceSelected: boolean
  isShowingInactiveUsers: boolean
  onInvite: () => void
  onManageCapacity: () => void
  onRestore: () => void
  onEditRole: () => void
  onManageSkills: () => void
  onRemove: () => void
}

/**
 * Actions menu for a single user.
 */
export function UserRowActions({
  publicId,
  canManageGlobalCapacity,
  isWorkspaceSelected,
  isShowingInactiveUsers,
  onInvite,
  onManageCapacity,
  onRestore,
  onEditRole,
  onManageSkills,
  onRemove,
}: UserRowActionsProps) {
  return (
    <Dropdown
      autoDismiss
      position="bottom-end"
      testId={`user-management-table-row-actions-${publicId}`}
      button={
        <IconButton name="more" label="User actions" color="var(--grey600)" />
      }
    >
      <DropdownTitle>Actions</DropdownTitle>
      <DropdownItem
        onPress={onInvite}
        icon={<Icon name="add" color="var(--grey600)" />}
        buttonTestId="user-management-table-row-actions-invite-to-workspace"
        muteClickEvent
      >
        Invite to workspace
      </DropdownItem>
      {canManageGlobalCapacity && (
        <DropdownItem
          onPress={onManageCapacity}
          icon={<Icon name="edit-outlined" color="var(--grey600)" />}
          buttonTestId="user-management-table-row-actions-manage-global-capacity"
          muteClickEvent
        >
          Manage global capacity
        </DropdownItem>
      )}
      {/* Only display these actions when filtering by a workspace. */}
      {isWorkspaceSelected && (
        <>
          {isShowingInactiveUsers ? (
            <DropdownItem
              onPress={onRestore}
              icon={<Icon name="add-people" color="var(--grey600)" />}
              buttonTestId="user-management-table-row-actions-restore-to-workspace"
              muteClickEvent
            >
              Restore to workspace
            </DropdownItem>
          ) : (
            <>
              <DropdownItem
                onPress={onManageCapacity}
                icon={<Icon name="edit-outlined" color="var(--grey600)" />}
                buttonTestId="user-management-table-row-actions-manage-capacity"
                muteClickEvent
              >
                Manage capacity
              </DropdownItem>
              <DropdownItem
                onPress={onEditRole}
                icon={<Icon name="edit-roles" color="var(--grey600)" />}
                buttonTestId="user-management-table-row-actions-edit-roles"
                muteClickEvent
              >
                Edit role
              </DropdownItem>
              <DropdownItem
                onPress={onManageSkills}
                icon={<Icon name="edit-skills" color="var(--grey600)" />}
                buttonTestId="user-management-table-row-actions-manage-skills"
                muteClickEvent
              >
                Manage skills
              </DropdownItem>
              <DropdownItem
                onPress={onRemove}
                icon={<Icon name="close" color="var(--grey600)" />}
                buttonTestId="user-management-table-row-actions-remove-from-workspace"
                muteClickEvent
              >
                Remove from workspace
              </DropdownItem>
            </>
          )}
        </>
      )}
    </Dropdown>
  )
}
